import { useEffect, useState } from "react";
import { Wallet, Hexagon, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { shortAddress } from "@/lib/wallet";
import { useArcConnect, safeWalletClick } from "@/context/ArcEcosystemContext";
import { formatConnectedSince } from "@/lib/connectedSince";
import { getAuthMe, AUTH_CHANGED_EVENT } from "@/lib/api";

/**
 * Single-row navbar.
 *
 * Desktop (md+):
 *   [logo]        [primary nav]                        [auth/wallet cluster]
 *   - logo: shrink-0
 *   - nav: flex-1, justify-end, gap-5 — never wraps (whitespace-nowrap + scrolling fallback)
 *   - cluster: shrink-0, gap-3
 *
 * Mobile:
 *   - nav hidden (existing pattern)
 *   - auth cluster shown compactly
 */

const NAV = [
  { label: "Platform", id: "about" },
  { label: "Holdings", id: "holdings" },
  { label: "Arenas", id: "arena-access" },
  { label: "Tokenomics", id: "tokenomics" },
  { label: "Roadmap", id: "roadmap" },
  { label: "FAQ", id: "faq" },
];

export default function Header() {
  const {
    address, connect, connecting, isConnected, isWrongNetwork, switchToBSC, connectedAt,
  } = useArcConnect();

  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        await getAuthMe();
        if (alive) setAuthed(true);
      } catch (_) {
        if (alive) setAuthed(false);
      }
    };
    probe();
    const onFocus = () => probe();
    const onAuthChanged = () => probe();
    window.addEventListener("focus", onFocus);
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    };
  }, []);

  const onConnectClick = () => safeWalletClick(
    isWrongNetwork ? switchToBSC : connect,
  );
  const connectLabel = connecting
    ? "Connecting…"
    : isWrongNetwork
      ? "Switch BSC"
      : isConnected
        ? shortAddress(address)
        : "Connect";
  const connectTitle = isConnected
    ? "Wallet connected — read-only access check"
    : "Connect your wallet (read-only) to check tier access";
  const sinceLabel = isConnected && !isWrongNetwork ? formatConnectedSince(connectedAt) : null;

  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-md bg-black/60"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">

        {/* ---- Logo (left, fixed width) ---- */}
        <a
          href="#top"
          className="flex items-center gap-2 group shrink-0"
          data-testid="logo-link"
        >
          <div className="relative w-9 h-9 grid place-items-center shrink-0">
            <Hexagon
              className="absolute inset-0 w-9 h-9 text-[#39FF14] group-hover:rotate-180 transition-transform duration-700"
              strokeWidth={1.5}
            />
            <span className="font-display text-[11px] font-black text-[#39FF14] glow-green">
              ARC
            </span>
          </div>
          <div className="leading-none hidden sm:block">
            <div className="font-display text-base font-black tracking-widest whitespace-nowrap">
              ARENACOIN
            </div>
            <div className="text-[10px] text-[#00F0FF] uppercase tracking-[0.3em] whitespace-nowrap">
              Arena Platform
            </div>
          </div>
        </a>

        {/* ---- Primary nav (center→right, flex-1 so it consumes the gap) ---- */}
        <nav className="hidden lg:flex flex-1 items-center justify-end gap-5 min-w-0">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`/#${n.id}`}
              className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-white/70 hover:text-[#39FF14] transition-colors"
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </a>
          ))}
          <Link
            to="/redeem"
            className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-[#00F0FF] hover:text-[#39FF14] transition-colors"
            data-testid="nav-redeem"
          >
            Redeem
          </Link>
          <Link
            to="/account"
            className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-[#00F0FF] hover:text-[#39FF14] transition-colors"
            data-testid="nav-account"
          >
            Account
          </Link>
        </nav>

        {/* ---- Auth + wallet cluster (right, fixed) ---- */}
        <div className="flex items-center gap-3 shrink-0 ml-auto lg:ml-0">
          {authed === false && (
            <Link
              to="/signin"
              className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] hover:text-[#39FF14] transition-colors"
              data-testid="nav-signin"
            >
              Sign in
            </Link>
          )}
          <div className="flex flex-col items-end gap-0.5">
            <button
              onClick={onConnectClick}
              disabled={connecting}
              data-testid="header-connect-wallet-button"
              data-connect-state={
                connecting
                  ? "connecting"
                  : isWrongNetwork
                    ? "wrong-network"
                    : isConnected
                      ? "connected"
                      : "disconnected"
              }
              title={connectTitle}
              className="whitespace-nowrap inline-flex items-center gap-2 rounded-md border border-[#39FF14]/60 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 hover:border-[#39FF14] text-[#39FF14] font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 disabled:opacity-60 transition-colors"
            >
              {connecting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Wallet className="w-3.5 h-3.5" />
              )}
              {connectLabel}
            </button>
            {sinceLabel && (
              <span
                className="hidden md:block font-mono text-[10px] tracking-[0.15em] text-white/40 leading-none"
                data-testid="header-connected-since"
              >
                {sinceLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
